import { fmt, withUnit } from './format';

// The two-column layout (Complete): header, specs cards, condition summary, price and equipment chips.

// vehicle fields by card; a field the cards do not know goes to Vehicle identification
const ENGINE_FIELDS = ['engineSize', 'engine', 'fuel', 'transmission', 'gearbox', 'drive', 'euro'];
const GENERAL_FIELDS = ['bodyType', 'doors', 'colour', 'region'];
export const CHIP_LIMIT = 7;

const fieldOf = (vehicle, key) => (vehicle.fields || []).find(f => f.key === key && f.value) || null;
const row = f => ({ key: f.key, label: f.label, value: String(f.value), sub: f.sub || '' });

// Header: "Volkswagen Golf" with the version beside it, then mileage and registration year
export const buildHero = (report, t) => {
    const vehicle = report.vehicle;
    if (!vehicle) return null;

    const versionField = fieldOf(vehicle, 'model');
    // the dealer documents give one model line ("Golf 1.5 eTSI"): it is the pink model, with no version beside it
    const model = vehicle.model || (versionField ? versionField.value : '');
    const version = vehicle.model ? versionField : null;
    const mileage = fieldOf(vehicle, 'mileage');
    const stats = report.stats || {};
    const registration = fieldOf(vehicle, 'registration');
    const year = registration && (String(registration.value).match(/\d{4}/) || [])[0];
    const mileageValue = mileage ? mileage.value : stats.lastKm != null && withUnit(fmt(stats.lastKm), 'km');
    const region = fieldOf(vehicle, 'region');

    return {
        vin: vehicle.vin,
        brand: vehicle.brand || '',
        model,
        version: version && version.value !== model ? version.value : '',
        // our inspection is "Verified"; a report from the dealer's documents alone says so instead
        badge: report.inspection ? 'verified' : 'dealer',
        stats: [
            mileageValue && { label: t('layout.mileage'), value: mileageValue },
            year && { label: t('layout.registrationYear'), value: year },
            // a report with no parsed data (waiting, original PDF) knows little more than the region
            !mileageValue && !year && region && { label: region.label, value: region.value },
        ].filter(Boolean),
    };
};

// Vehicle identification, Engine & transmission, General specifications; a card without fields is left out
export const buildSpecs = (vehicle, t) => {
    if (!vehicle) return [];

    const fields = (vehicle.fields || []).filter(f => f.value != null && f.value !== '');
    const of = keys =>
        keys
            .map(key => fields.find(f => f.key === key))
            .filter(Boolean)
            .map(row);
    const known = [...ENGINE_FIELDS, ...GENERAL_FIELDS];
    const rest = fields.filter(f => !known.includes(f.key)).map(row);
    const modelAt = rest.findIndex(f => f.key === 'model');
    const vin = vehicle.vin ? [{ key: 'vin', label: t('layout.vin'), value: vehicle.vin, sub: '', mono: true }] : [];
    const identification = [
        ...(vehicle.brand ? [{ key: 'make', label: t('layout.make'), value: vehicle.brand, sub: '' }] : []),
        ...rest.slice(0, modelAt + 1),
        ...vin,
        ...rest.slice(modelAt + 1),
    ];

    return [
        { key: 'identification', title: t('layout.identification'), rows: identification, wide: true },
        { key: 'engine', title: t('layout.engineTransmission'), rows: of(ENGINE_FIELDS) },
        { key: 'general', title: t('layout.generalSpecs'), rows: of(GENERAL_FIELDS) },
    ].filter(card => card.rows.length > 0);
};

// Condition summary: service book, owners, accident flag; only what the inspection recorded
export const buildCondition = (condition, t) => {
    if (!condition) return null;

    const rows = [
        condition.serviceBook && { label: t('layout.serviceHistory'), value: condition.serviceBook },
        condition.owners != null && {
            label: t('layout.ownership'),
            value: t('layout.previousOwners', { count: condition.owners }),
        },
        condition.accidents != null && {
            label: t('layout.accidentStatus'),
            value: condition.accidents ? t('layout.accidentsReported') : t('layout.accidentFree'),
            tone: condition.accidents ? 'warning' : 'success',
        },
    ].filter(Boolean);

    return rows.length ? rows : null;
};

// Service summary (dealer documents, no inspection): visits, period, countries, bodywork, the mileage check
export const buildServiceSummary = (report, mileage, t) => {
    const stats = report.stats || {};
    const visits = (report.timeline && report.timeline.visits) || [];
    const countries = Array.from(new Set(visits.map(v => v.country).filter(Boolean)));
    const bodywork = visits.filter(v => (v.tags || []).includes('Bodywork / paint')).length;

    const rows = [
        stats.visits != null && { label: t('layout.dealerVisits'), value: String(stats.visits) },
        stats.period && { label: t('tiles.period'), value: stats.period },
        countries.length > 0 && { label: t('layout.countries'), value: countries.join(', ') },
        visits.length > 0 && {
            label: t('layout.bodywork'),
            value: t('history.visitCount', { count: bodywork }),
            ...(bodywork > 0 ? { tone: 'warning' } : {}),
        },
        mileage && {
            label: t('layout.mileageCheck'),
            value: mileage.drops.length ? t('layout.rollback') : t('layout.noRollback'),
            tone: mileage.drops.length ? 'error' : 'success',
        },
    ].filter(Boolean);

    return rows.length ? rows : null;
};

// Price card; the location flag only for a two-letter country code
export const buildPrice = pricing => {
    if (!pricing || !pricing.amount) return null;

    const country = String(pricing.country || '');
    return {
        amount: pricing.amount,
        city: pricing.city || '',
        flag: /^[a-z]{2}$/i.test(country) ? country.toLowerCase() : '',
    };
};

// Equipment as chips: the first few, the rest behind "Show more" (always printed)
export const buildChips = (equipment, { allCodes }) => {
    const codes = (equipment && equipment.codes) || [];
    if (!codes.length) return null;

    return {
        chips: codes.map((c, i) => ({
            code: c.code || '',
            desc: c.desc || '',
            hidden: i >= CHIP_LIMIT && !allCodes,
        })),
        more: Math.max(0, codes.length - CHIP_LIMIT),
        expanded: !!allCodes,
    };
};
